import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useCreateServiceListing } from '../../../hooks/DataFetching/useCreateServiceListing';
import { useFetchIndustries } from '../../../hooks/DataFetching/useFetchIndustries';
import { StatusMessage } from '../../ui/StatusMessage';
import '../../../styles/components/AddServiceModal.css';

const AddServiceModal = ({ show, handleClose, onServiceCreated }) => {
    const { createService } = useCreateServiceListing();
    const { industries } = useFetchIndustries();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [price, setPrice] = useState('');
    const [industryId, setIndustryId] = useState('');
    const [image, setImage] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setSubmitError(null);
        const formData = new FormData();
        formData.append('Title', title);
        formData.append('Description', description);
        if (price) formData.append('Price', price);
        formData.append('IndustryId', industryId);
        if (image) formData.append('Image', image);
        try {
            await createService(formData);
            onServiceCreated();
            setTitle('');
            setDescription('');
            setPrice('');
            setIndustryId('');
            setImage(null);
        } catch (err) {
            console.error('Error creating service:', err);
            setSubmitError('Could not create this service listing. Please review the details and retry.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} className="add-service-modal tyk-modal" centered size="lg">
            <Modal.Header closeButton closeVariant="white">
                <div>
                    <span className="tyk-kicker">Create listing</span>
                    <Modal.Title>Add Service</Modal.Title>
                </div>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {submitError && (
                        <div className="mb-3">
                            <StatusMessage
                                tone="error"
                                title="Service not created"
                                message={submitError}
                            />
                        </div>
                    )}
                    <Form.Group controlId="serviceTitle" className="mb-3">
                        <Form.Label>Title</Form.Label>
                        <Form.Control
                            type="text"
                            className="tyk-input"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            disabled={submitting}
                            required
                        />
                    </Form.Group>
                    <Form.Group controlId="serviceDescription" className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            className="tyk-textarea"
                            rows={3}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            disabled={submitting}
                            required
                        />
                    </Form.Group>
                    <Form.Group controlId="servicePrice" className="mb-3">
                        <Form.Label>Price</Form.Label>
                        <Form.Control
                            type="number"
                            className="tyk-input"
                            step="0.01"
                            value={price}
                            onChange={(e) => setPrice(e.target.value)}
                            disabled={submitting}
                        />
                    </Form.Group>
                    <Form.Group controlId="serviceIndustry" className="mb-3">
                        <Form.Label>Industry</Form.Label>
                        <Form.Control
                            as="select"
                            className="tyk-select"
                            value={industryId}
                            onChange={(e) => setIndustryId(e.target.value)}
                            disabled={submitting}
                            required
                        >
                            <option value="">Select an industry</option>
                            {industries.map((ind) => (
                                <option key={ind.id} value={ind.id}>
                                    {ind.name}
                                </option>
                            ))}
                        </Form.Control>
                    </Form.Group>
                    <Form.Group controlId="serviceImage" className="mb-3">
                        <Form.Label>Image</Form.Label>
                        <Form.Control
                            type="file"
                            className="tyk-input"
                            onChange={(e) => setImage(e.target.files[0])}
                            disabled={submitting}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit" disabled={submitting}>
                        {submitting ? 'Saving...' : 'Save'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default AddServiceModal;
